import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Phone, Loader2, ExternalLink } from "lucide-react";

type PartnerMiniData = {
  leadId: string | null;
  partnerId: string;
  name: string;
  title: string;
  location: string;
  phone: string;
  mapsUrl: string | null;
  cover: {
    type: string;
    label: string;
    url?: string;
  };
  description: string;
};

const PartnerMiniPage: React.FC = () => {
  const { leadId, partnerId } = useParams<{ leadId?: string; partnerId?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PartnerMiniData | null>(null);

  const telHref = useMemo(() => {
    if (!data?.phone) return "";
    const cleanPhone = data.phone.replace(/\s+/g, "");
    return `tel:${cleanPhone}`;
  }, [data?.phone]);

  const trackEvent = async (event: "view" | "call_click" | "maps_click", payload?: PartnerMiniData) => {
    const current = payload || data;
    if (!current) return;

    try {
      if (!current.leadId) return;

      await fetch("/api/track-partner-mini.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: current.leadId,
          partnerId: current.partnerId,
          event,
        }),
        keepalive: true,
      });
    } catch (err) {
      console.error("Tracking error:", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!leadId && !partnerId) {
        setError("Μη έγκυρο link");
        setLoading(false);
        return;
      }

      try {
        const query = leadId
          ? `leadId=${encodeURIComponent(leadId)}`
          : `partnerId=${encodeURIComponent(partnerId as string)}`;

        const response = await fetch(`/api/get-partner-mini.php?${query}`);
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.error || "Δεν βρέθηκαν στοιχεία");
        }

        setData(result);
        if (result?.leadId) {
          await trackEvent("view", result);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Σφάλμα φόρτωσης");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [leadId, partnerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-3 text-slate-600">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p>Φόρτωση στοιχείων συνεργάτη...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center space-y-3">
            <h1 className="text-lg font-semibold text-slate-900">Δεν βρέθηκαν στοιχεία συνεργάτη</h1>
            <p className="text-sm text-slate-600">{error || "Δοκιμάστε ξανά αργότερα."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-md mx-auto pb-8">
        <div className="bg-white border-b border-slate-200">
          <div className="px-4 pt-5 pb-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Partner Mini Page</p>
          </div>

          <div className="mx-4 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
            {data.cover?.type === "image" && data.cover?.url ? (
              <div className="h-56 bg-slate-900 flex items-center justify-center">
                <img
                  src={data.cover.url}
                  alt={data.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="h-44 bg-gradient-to-r from-emerald-500 to-green-600" />
            )}
          </div>

          <div className="px-4 pb-5 -mt-6 relative">
            <div className="h-14 w-14 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-lg font-semibold text-slate-700">
              {data.cover?.label || data.name.charAt(0)}
            </div>
            <h1 className="text-xl font-bold text-slate-900 mt-3">{data.name}</h1>
            <p className="text-sm text-slate-600 mt-1">{data.title}</p>

            <div className="flex items-start gap-2 mt-3 text-slate-600">
              <MapPin className="h-4 w-4 mt-0.5" />
              <p className="text-sm leading-relaxed">{data.location || "Δεν έχει δηλωθεί τοποθεσία"}</p>
            </div>
          </div>
        </div>

        <div className="px-4 mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm text-slate-700 leading-relaxed">{data.description}</p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-2">
            {telHref ? (
              <Button asChild className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base">
                <a
                  href={telHref}
                  onClick={() => trackEvent("call_click")}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Κλήση συνεργάτη
                </a>
              </Button>
            ) : (
              <Button className="w-full h-12 text-base" disabled>
                <Phone className="mr-2 h-5 w-5" />
                Κλήση συνεργάτη
              </Button>
            )}

            {data.mapsUrl && (
              <Button variant="outline" asChild className="w-full h-12 text-base border-slate-300">
                <a
                  href={data.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent("maps_click")}
                >
                  <MapPin className="mr-2 h-5 w-5" />
                  Άνοιγμα στο Google Maps
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerMiniPage;
